/* Shared pixel algorithm from Studio adjust-sheet/pipeline.ts. */
(()=>{
const NEUTRAL = {
    brightness: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    saturation: 0,
    temperature: 0,
    sepia: 0,
};
const clamp = (v, min, max) => (v < min ? min : v > max ? max : v);
const isNeutral = (a) => Object.keys(NEUTRAL).every(k => a[k] === NEUTRAL[k]);
/**
 * コントラストの 256 段トーンカーブ。
 * macOS Preview に合わせ、各チャンネルへ S 字カーブ(シグモイド)を当てる。
 * per-channel なので上げると色が濃く鮮やかになり(パンチ)、暗部が沈む。シグモイドは端で
 * なだらかにロールオフするので白飛び・黒潰れしにくく、ハイライトの白は白のまま保たれる。
 * マイナス側は中間へ寄せてフラットにする。
 */
function buildContrastLUT(contrast) {
    const lut = new Uint8ClampedArray(256);
    if (contrast > 0) {
        // macOS(Core Image)はリニア光で処理するため、sRGB→リニア→S字→sRGB の順で当てる。
        // ガンマ空間のまま当てると明るい画像は白く飛ぶだけで、Mac のように中間調・影が沈まない。
        // リニア空間ではピボット(0.5)が sRGB の約 0.74 に相当し、中間調が力強く暗側へ落ちる。
        const k = Math.pow(contrast / 100, 1.3) * 22;
        const sig = (x) => 1 / (1 + Math.exp(-k * (x - 0.5)));
        const s0 = sig(0);
        const s1 = sig(1);
        for (let i = 0; i < 256; i++) {
            const n = i / 255;
            const lin = n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
            const mapped = (sig(lin) - s0) / (s1 - s0);
            const out = mapped <= 0.0031308 ? 12.92 * mapped : 1.055 * Math.pow(mapped, 1 / 2.4) - 0.055;
            lut[i] = out * 255;
        }
    }
    else if (contrast < 0) {
        // 中間へ寄せてフラットにする。ただし下限を設け、最小でも単色グレーにならないようにする。
        const factor = 1 + (contrast / 100) * 0.65; // -100 → 0.35
        for (let i = 0; i < 256; i++) {
            const n = i / 255;
            lut[i] = (0.5 + (n - 0.5) * factor) * 255;
        }
    }
    return lut;
}
/**
 * 明るさ(加算) → コントラスト を 1 本の 256 段 LUT に畳み込む。
 * どちらもチャンネル独立なので、後段の処理がないときは 1 回の参照で置き換えられる。
 */
function buildToneLUT(bAdd, contrast) {
    const contrastLUT = contrast !== 0 ? buildContrastLUT(contrast) : null;
    const lut = new Uint8ClampedArray(256);
    for (let i = 0; i < 256; i++) {
        const shifted = i + bAdd;
        lut[i] = contrastLUT ? contrastLUT[shifted < 0 ? 0 : shifted > 255 ? 255 : shifted | 0] : shifted;
    }
    return lut;
}
/**
 * ライブラリを使わない Canvas ピクセル処理エンジン。
 * src(元画像のピクセル)から dst(出力)へ、各調整を順に適用する。
 * プレビューと保存で同じ関数を通すため、見た目と書き出しが完全一致する。
 */
function applyPipeline(src, dst, a) {
    const bAdd = a.brightness * 1.5;
    const sat = a.saturation / 100 + 1;
    const t = a.temperature / 100;
    const sep = a.sepia / 100;
    const hl = a.highlights / 100;
    const sh = a.shadows / 100;
    const needsLum = hl !== 0 || sh !== 0;
    const needsSat = sat !== 1;
    // 明るさ / コントラストだけの操作(スライダー 1 本を動かす大半のケース)は
    // チャンネル独立なので LUT 参照だけで済ませる。画素あたりの浮動小数演算が消えて数倍速い。
    if (!needsLum && !needsSat && t === 0 && sep === 0) {
        if (bAdd === 0 && a.contrast === 0) {
            dst.set(src);
            return;
        }
        const lut = buildToneLUT(bAdd, a.contrast);
        for (let i = 0; i < src.length; i += 4) {
            dst[i] = lut[src[i]];
            dst[i + 1] = lut[src[i + 1]];
            dst[i + 2] = lut[src[i + 2]];
            dst[i + 3] = src[i + 3];
        }
        return;
    }
    const contrastActive = a.contrast !== 0;
    const contrastLUT = contrastActive ? buildContrastLUT(a.contrast) : null;
    for (let i = 0; i < src.length; i += 4) {
        let r = src[i];
        let g = src[i + 1];
        let b = src[i + 2];
        // 明るさ(加算)
        r += bAdd;
        g += bAdd;
        b += bAdd;
        // コントラスト(per-channel の S 字トーンカーブ)
        if (contrastLUT) {
            r = contrastLUT[r < 0 ? 0 : r > 255 ? 255 : r | 0];
            g = contrastLUT[g < 0 ? 0 : g > 255 ? 255 : g | 0];
            b = contrastLUT[b < 0 ? 0 : b > 255 ? 255 : b | 0];
        }
        if (needsLum) {
            const lum = 0.299 * r + 0.587 * g + 0.114 * b;
            // ハイライト(明部)
            if (hl !== 0) {
                const w = clamp((lum - 128) / 127, 0, 1);
                const adj = hl * 80 * w;
                r += adj;
                g += adj;
                b += adj;
            }
            // シャドウ(暗部)
            if (sh !== 0) {
                const w = clamp((128 - lum) / 128, 0, 1);
                const adj = sh * 80 * w;
                r += adj;
                g += adj;
                b += adj;
            }
        }
        // 彩度
        if (needsSat) {
            const gray = 0.299 * r + 0.587 * g + 0.114 * b;
            r = gray + (r - gray) * sat;
            g = gray + (g - gray) * sat;
            b = gray + (b - gray) * sat;
        }
        // 色温度(暖=R+ / B-)
        r += t * 30;
        b -= t * 30;
        // セピア(ブレンド)
        if (sep > 0) {
            const sr = r * 0.393 + g * 0.769 + b * 0.189;
            const sg = r * 0.349 + g * 0.686 + b * 0.168;
            const sb = r * 0.272 + g * 0.534 + b * 0.131;
            r = r * (1 - sep) + sr * sep;
            g = g * (1 - sep) + sg * sep;
            b = b * (1 - sep) + sb * sep;
        }
        dst[i] = clamp(r, 0, 255);
        dst[i + 1] = clamp(g, 0, 255);
        dst[i + 2] = clamp(b, 0, 255);
        dst[i + 3] = src[i + 3];
    }
}

window.PyUploadAdjustments={applyPipeline,NEUTRAL};
})();